import type { SegmentedChoicesData } from '../../types';
import { getPercentageGetter } from '../../utils/getPercentageGetter';

/**
 * Identifies the first and the last non-zero index in the scores array
 * (which is needed to identify the first and the last segment in the bar,
 * which is needed because they have different border radius)
 */
export const getMinMaxIndexes = (data: SegmentedChoicesData[]) =>
  data.map(({ id, scores }, i) => {
    const firstNonZeroIndex = scores.findIndex((score) => score > 0);
    const lastIndexNonZero = scores.reduce(
      (maxIndex, score, i) => (score !== 0 ? i : maxIndex),
      -1,
    );
    return {
      id,
      i,
      minIndex: firstNonZeroIndex,
      maxIndex: lastIndexNonZero,
    };
  });

const getBarTotal = (data: SegmentedChoicesData | undefined) => {
  if (!data) {
    return 0;
  }
  return data.scores.reduce((acc, score) => acc + score, 0);
};

export const getBarTotalById = (
  segmentedChoiceId: SegmentedChoicesData['id'],
  data: SegmentedChoicesData[],
) =>
  getBarTotal(
    data.find((segmentedChoice) => segmentedChoice.id === segmentedChoiceId),
  );

export const getBarPercentageGetter = (
  data: SegmentedChoicesData[],
  numberOfRespondents: number,
) => {
  const getPercentage = getPercentageGetter(numberOfRespondents);
  return (segmentedChoiceId: SegmentedChoicesData['id']) => {
    return (
      getPercentage(getBarTotalById(segmentedChoiceId, data)).toFixed(0) + '%'
    );
  };
};

export const getSegmentPercentage = (
  segmentValue: number,
  segmentedChoiceId: SegmentedChoicesData['id'],
  data: SegmentedChoicesData[],
) => {
  const barTotalScore = getBarTotalById(segmentedChoiceId, data);
  if (barTotalScore === 0) {
    return '0%';
  }
  const percentage = 100 * (segmentValue / barTotalScore);
  return percentage.toFixed(0) + '%';
};
